import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppModal from '../components/AppModal';
import api from '../services/api';
import { listarCajas, obtenerSaldoCaja } from '../services/cajaService';
import {
  abrirPdfRecibo,
  generarPdfRecibo
} from '../utils/abrirDocumento';

function fechaHoyInput() {
  const fecha = new Date();
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function normalizarFechaInput(valor) {
  const texto = String(valor || '').trim();

  return /^\d{4}-\d{2}-\d{2}/.test(texto)
    ? texto.slice(0, 10)
    : '';
}

function subtitulo(texto) {
  const textoFormateado =
    texto.charAt(0).toUpperCase() + texto.slice(1);

  return (
    <div
      style={{
        fontSize: '13px',
        color: '#f9575b',
        marginTop: '2px',
        marginBottom: '-2px',
        fontWeight: '600',
        letterSpacing: '0.3px'
      }}
    >
      {textoFormateado}
    </div>
  );
}

function etiqueta(texto, obligatorio = false) {
  return (
    <label className="form-label">
      {texto}
      {obligatorio && (
        <span style={{ color: '#dc3545', marginLeft: '4px' }}>
          *
        </span>
      )}
    </label>
  );
}

function normalizar(texto) {
  return String(texto || '')
    .trim()
    .toUpperCase();
}

function buscarCatalogoId(catalogos, tipo, valor) {
  if (!valor) return '';

  const encontrado = catalogos.find(item =>
    item.Tipo === tipo &&
    normalizar(item.Valor) === normalizar(valor)
  );

  return encontrado ? String(encontrado.Id) : '';
}

function buscarEmpresaId(empresas, nombre) {
  if (!nombre || nombre === '-') return '';

  const encontrada = empresas.find(item =>
    normalizar(item.Nombre) === normalizar(nombre)
  );

  return encontrada ? String(encontrada.Id) : '';
}

function buscarPersonaId(personas, nombre, tipo) {
  if (!nombre || nombre === '-') return '';

  const encontrada = personas.find(item =>
    item.Tipo === tipo &&
    normalizar(item.Nombre) === normalizar(nombre)
  );

  return encontrada ? String(encontrada.Id) : '';
}

function campoAutocomplete({
  id,
  label,
  obligatorio,
  value,
  onChange,
  opciones,
  placeholder,
  disabled = false,
  ayuda
}) {
  return (
    <>
      {etiqueta(label, obligatorio)}

      <input
        className="form-control"
        list={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />

      <datalist id={id}>
        {opciones.map(item => (
          <option
            key={item.Id}
            value={item.Nombre || item.Valor}
          />
        ))}
      </datalist>

      {ayuda && (
        <div className="form-text">
          {ayuda}
        </div>
      )}
    </>
  );
}

function RecibosPage() {
  const location = useLocation();

  const [catalogos, setCatalogos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [personas, setPersonas] = useState([]);

  const [mensaje, setMensaje] = useState('');
  const [reciboCreado, setReciboCreado] = useState(null);
  const [erroresValidacion, setErroresValidacion] = useState([]);
  const [reciboBaseDuplicado, setReciboBaseDuplicado] = useState(null);
  const [generandoPreview, setGenerandoPreview] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    abierto: false,
    mensaje: ''
  });

  const [form, setForm] = useState({
    Fecha: fechaHoyInput(),
    TipoReciboId: '',
    FormaPagoId: '',
    NumeroReferencia: '',
    LugarExpedicionId: '',
    EmpresaEmiteId: '',
    PersonaEmiteId: '',
    EmpresaRecibeId: '',
    PersonaRecibeId: '',
    PersonaRecibeNombreLibre: '',
    ConceptoId: '',
    MonedaId: '',
    Importe: '',
    CantidadLetra: '',
    FechaInicio: '',
    FechaFin: '',
    Observaciones: '',
    CajaId: ''
  });

  const [textoAuto, setTextoAuto] = useState({
    EmpresaEmite: '',
    PersonaEmite: '',
    EmpresaRecibe: '',
    PersonaRecibe: ''
  });

  const [cajasDisponibles, setCajasDisponibles] = useState([]);
  const [saldoCaja, setSaldoCaja] = useState(null);

  const personasEmisoras = useMemo(() => {
    const mapa = new Map();

    personas
      .filter(item =>
        item.Tipo === 'Emisor' &&
        item.Activo
      )
      .forEach(item => {
        const clave = normalizar(item.Nombre);

        if (!mapa.has(clave)) {
          mapa.set(clave, item);
        }
      });

    return Array.from(mapa.values())
      .sort((a, b) =>
        a.Nombre.localeCompare(b.Nombre)
      );
  }, [personas]);

  const personasReceptoras = useMemo(() => {
    const mapa = new Map();

    personas
      .filter(item =>
        item.Tipo === 'Receptor' &&
        item.Activo
      )
      .forEach(item => {
        const clave = normalizar(item.Nombre);

        if (!mapa.has(clave)) {
          mapa.set(clave, item);
        }
      });

    return Array.from(mapa.values())
      .sort((a, b) =>
        a.Nombre.localeCompare(b.Nombre)
      );
  }, [personas]);

  const formaPagoSeleccionada = useMemo(() => {
    return catalogos.find(item => String(item.Id) === String(form.FormaPagoId));
  }, [catalogos, form.FormaPagoId]);

  const formaPagoEsEfectivo = useMemo(() => {
    return normalizar(formaPagoSeleccionada?.Valor)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes('EFECTIVO');
  }, [formaPagoSeleccionada]);

  const cajaSeleccionada = useMemo(() => {
    return cajasDisponibles.find(item => String(item.Id) === String(form.CajaId));
  }, [cajasDisponibles, form.CajaId]);

  const saldoPosteriorCaja = formaPagoEsEfectivo && saldoCaja !== null
    ? Number(saldoCaja || 0) - Number(form.Importe || 0)
    : null;

  const empresasActivas = useMemo(() => {
    return empresas
      .filter(item => item.Activo !== false)
      .sort((a, b) =>
        String(a.Nombre).localeCompare(String(b.Nombre))
      );
  }, [empresas]);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const duplicarId = params.get('duplicar');

    if (
      duplicarId &&
      catalogos.length > 0 &&
      empresas.length > 0 &&
      personas.length > 0
    ) {
      cargarReciboDuplicado(duplicarId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, catalogos, empresas, personas]);

  useEffect(() => {
    async function cargarCajasParaRecibo() {
      if (!formaPagoEsEfectivo || !form.MonedaId) {
        setCajasDisponibles([]);
        setSaldoCaja(null);
        setForm(prev => prev.CajaId ? { ...prev, CajaId: '' } : prev);
        return;
      }

      try {
        const cajas = await listarCajas({ activa: 1, monedaId: form.MonedaId });
        setCajasDisponibles(cajas);

        if (form.CajaId && !cajas.some(caja => String(caja.Id) === String(form.CajaId))) {
          setSaldoCaja(null);
          setForm(prev => ({ ...prev, CajaId: '' }));
          return;
        }

        if (form.CajaId) {
          const saldo = await obtenerSaldoCaja(form.CajaId);
          setSaldoCaja(Number(saldo.saldoActual || 0));
        } else {
          setSaldoCaja(null);
        }
      } catch (error) {
        setMensaje(error.response?.data?.mensaje || error.message);
      }
    }

    cargarCajasParaRecibo();
  }, [formaPagoEsEfectivo, form.MonedaId, form.CajaId]);
  async function cargarDatos() {
    try {
      const [catRes, empRes, perRes] = await Promise.all([
        api.get('/catalogos'),
        api.get('/empresas'),
        api.get('/personas')
      ]);

      const catalogosData = catRes.data;

      setCatalogos(catalogosData);
      setEmpresas(empRes.data);
      setPersonas(perRes.data);

      const lugarMonterrey = catalogosData.find(
        item =>
          item.Tipo === 'Lugar de Expedición' &&
          normalizar(item.Valor).includes('MONTERREY')
      );

      const tipoPago = catalogosData.find(
        item =>
          item.Tipo === 'Tipo de Recibo' &&
          normalizar(item.Valor) === 'PAGO'
      );

      setForm(prev => ({
        ...prev,
        LugarExpedicionId: lugarMonterrey
          ? String(lugarMonterrey.Id)
          : prev.LugarExpedicionId,
        TipoReciboId: tipoPago
          ? String(tipoPago.Id)
          : prev.TipoReciboId
      }));
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  async function cargarReciboDuplicado(id) {
    try {
      const response = await api.get(`/recibos/${id}`);
      const recibo = response.data;

      const tipoReciboId =
        recibo.TipoReciboId ||
        buscarCatalogoId(catalogos, 'Tipo de Recibo', recibo.TipoRecibo);

      const formaPagoId =
        recibo.FormaPagoId ||
        buscarCatalogoId(catalogos, 'Forma de pago', recibo.FormaPago);

      const lugarExpedicionId =
        recibo.LugarExpedicionId ||
        buscarCatalogoId(catalogos, 'Lugar de Expedición', recibo.LugarExpedicion);

      const conceptoId =
        recibo.ConceptoId ||
        buscarCatalogoId(catalogos, 'Concepto', recibo.Concepto);

      const monedaId =
        recibo.MonedaId ||
        buscarCatalogoId(catalogos, 'Moneda', recibo.Moneda);

      const empresaEmiteId =
        recibo.EmpresaEmiteId ||
        buscarEmpresaId(empresas, recibo.EmpresaEmite);

      const empresaRecibeId =
        recibo.EmpresaRecibeId ||
        buscarEmpresaId(empresas, recibo.EmpresaRecibe);

      const personaEmiteId =
        recibo.PersonaEmiteId ||
        buscarPersonaId(personas, recibo.PersonaEmite, 'Emisor');

      const personaRecibeId =
        recibo.PersonaRecibeId ||
        buscarPersonaId(personas, recibo.PersonaRecibe, 'Receptor');

      const personaRecibeLibre =
        recibo.PersonaRecibeNombreLibre || '';

      setForm(prev => ({
        ...prev,
        Fecha: fechaHoyInput(),
        TipoReciboId: String(tipoReciboId || ''),
        FormaPagoId: String(formaPagoId || ''),
        NumeroReferencia: recibo.NumeroReferencia || '',
        LugarExpedicionId: String(lugarExpedicionId || ''),
        EmpresaEmiteId: String(empresaEmiteId || ''),
        PersonaEmiteId: String(personaEmiteId || ''),
        EmpresaRecibeId: String(empresaRecibeId || ''),
        PersonaRecibeId: personaRecibeLibre
          ? ''
          : String(personaRecibeId || ''),
        PersonaRecibeNombreLibre: personaRecibeLibre,
        ConceptoId: String(conceptoId || ''),
        MonedaId: String(monedaId || ''),
        Importe: recibo.Importe || '',
        CantidadLetra: recibo.CantidadLetra || '',
        FechaInicio: normalizarFechaInput(recibo.FechaInicio),
        FechaFin: normalizarFechaInput(recibo.FechaFin),
        Observaciones: recibo.Observaciones || '',
        CajaId: ''
      }));

      setTextoAuto({
        EmpresaEmite: recibo.EmpresaEmite || '',
        PersonaEmite: recibo.PersonaEmite || '',
        EmpresaRecibe: recibo.EmpresaRecibe || '',
        PersonaRecibe: personaRecibeLibre || recibo.PersonaRecibe || ''
      });

      setReciboCreado(null);
      setMensaje(`Nuevo recibo basado en ${recibo.Folio}`);
      setReciboBaseDuplicado({
        Id: recibo.Id,
        Folio: recibo.Folio
      });

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  function filtrarCatalogo(tipo) {
    const mapaTipos = {
      TipoRecibo: 'Tipo de Recibo',
      LugarExpedicion: 'Lugar de Expedición',
      FormaPago: 'Forma de pago',
      Concepto: 'Concepto',
      Moneda: 'Moneda'
    };

    const tipoReal = mapaTipos[tipo] || tipo;

    if (tipoReal !== 'Concepto') {
      return catalogos.filter(item =>
        item.Tipo === tipoReal && item.Activo
      );
    }

    if (!form.TipoReciboId) {
      return [];
    }

    return catalogos.filter(item =>
      item.Tipo === 'Concepto' &&
      item.Activo &&
      Number(item.CatalogoPadreId) === Number(form.TipoReciboId)
    );
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;

    if (name === 'TipoReciboId') {
      setForm(prev => ({
        ...prev,
        TipoReciboId: value,
        ConceptoId: ''
      }));

      return;
    }

    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  function actualizarEmpresaEmite(texto) {
    const encontrada = empresasActivas.find(item =>
      normalizar(item.Nombre) === normalizar(texto)
    );

    setTextoAuto(prev => ({
      ...prev,
      EmpresaEmite: texto
    }));

    setForm(prev => ({
      ...prev,
      EmpresaEmiteId: encontrada
        ? String(encontrada.Id)
        : ''
    }));
  }

  function actualizarEmpresaRecibe(texto) {
    const encontrada = empresasActivas.find(item =>
      normalizar(item.Nombre) === normalizar(texto)
    );

    setTextoAuto(prev => ({
      ...prev,
      EmpresaRecibe: texto
    }));

    setForm(prev => ({
      ...prev,
      EmpresaRecibeId: encontrada
        ? String(encontrada.Id)
        : ''
    }));
  }

  function actualizarPersonaEmite(texto) {
    const encontrada = personasEmisoras.find(item =>
      normalizar(item.Nombre) === normalizar(texto)
    );

    setTextoAuto(prev => ({
      ...prev,
      PersonaEmite: texto
    }));

    setForm(prev => ({
      ...prev,
      PersonaEmiteId: encontrada
        ? String(encontrada.Id)
        : ''
    }));
  }

  function actualizarPersonaRecibe(texto) {
    const encontrada = personasReceptoras.find(item =>
      normalizar(item.Nombre) === normalizar(texto)
    );

    setTextoAuto(prev => ({
      ...prev,
      PersonaRecibe: texto
    }));

    setForm(prev => ({
      ...prev,
      PersonaRecibeId: encontrada
        ? String(encontrada.Id)
        : '',
      PersonaRecibeNombreLibre: encontrada
        ? ''
        : texto.trim()
    }));
  }

  function validarAntesDeGuardar() {
    const errores = [];

    const tieneEmpresaEmite = Boolean(form.EmpresaEmiteId);
    const tienePersonaEmite = Boolean(form.PersonaEmiteId);

    const tieneEmpresaRecibe = Boolean(form.EmpresaRecibeId);
    const tienePersonaRecibe = Boolean(form.PersonaRecibeId);
    const tienePersonaRecibeLibre = Boolean(
      form.PersonaRecibeNombreLibre.trim()
    );

    if (!form.Fecha) {
      errores.push('Fecha es obligatoria.');
    }

    if (!form.TipoReciboId) {
      errores.push('Tipo de recibo es obligatorio.');
    }

    if (!form.FormaPagoId) {
      errores.push('Forma de pago es obligatoria.');
    }

    if (!form.LugarExpedicionId) {
      errores.push('Lugar de expedición es obligatorio.');
    }

    if (!tieneEmpresaEmite && !tienePersonaEmite) {
      errores.push('Debe seleccionar Empresa o Persona en Emite.');
    }

    if (
      !tieneEmpresaRecibe &&
      !tienePersonaRecibe &&
      !tienePersonaRecibeLibre
    ) {
      errores.push('Debe seleccionar Empresa, Persona o capturar Persona ocasional en Recibe.');
    }

    if (!form.ConceptoId) {
      errores.push('Concepto es obligatorio.');
    }

    if (!form.MonedaId) {
      errores.push('Moneda es obligatoria.');
    }

    if (!form.Importe || Number(form.Importe) <= 0) {
      errores.push('Importe es obligatorio y debe ser mayor a cero.');
    }

    if (formaPagoEsEfectivo) {
      if (!form.CajaId) {
        errores.push('Debe seleccionar una caja para recibos en efectivo.');
      }

      if (saldoPosteriorCaja !== null && saldoPosteriorCaja < 0 && !cajaSeleccionada?.PermitirSaldoNegativo) {
        errores.push('Saldo insuficiente en la caja seleccionada.');
      }
    }

    return errores;
  }

  function limpiarFormularioConDefaults() {
    const lugarMonterrey = catalogos.find(
      item =>
        item.Tipo === 'Lugar de Expedición' &&
        normalizar(item.Valor).includes('MONTERREY')
    );

    const tipoPago = catalogos.find(
      item =>
        item.Tipo === 'Tipo de Recibo' &&
        normalizar(item.Valor) === 'PAGO'
    );

    setForm({
      Fecha: fechaHoyInput(),
      TipoReciboId: tipoPago ? String(tipoPago.Id) : '',
      FormaPagoId: '',
      NumeroReferencia: '',
      LugarExpedicionId: lugarMonterrey ? String(lugarMonterrey.Id) : '',
      EmpresaEmiteId: '',
      PersonaEmiteId: '',
      EmpresaRecibeId: '',
      PersonaRecibeId: '',
      PersonaRecibeNombreLibre: '',
      ConceptoId: '',
      MonedaId: '',
      Importe: '',
      CantidadLetra: '',
      FechaInicio: '',
      FechaFin: '',
      Observaciones: '',
      CajaId: ''
    });

    setTextoAuto({
      EmpresaEmite: '',
      PersonaEmite: '',
      EmpresaRecibe: '',
      PersonaRecibe: ''
    });

    setReciboCreado(null);
    setReciboBaseDuplicado(null);
  }

  function construirPayloadRecibo(incluirDuplicado = false) {
    return {
      ...form,
      Fecha: normalizarFechaInput(form.Fecha) || null,
      TipoReciboId: Number(form.TipoReciboId),
      FormaPagoId: Number(form.FormaPagoId),
      LugarExpedicionId: Number(form.LugarExpedicionId),
      EmpresaEmiteId: form.EmpresaEmiteId
        ? Number(form.EmpresaEmiteId)
        : null,
      PersonaEmiteId: form.PersonaEmiteId
        ? Number(form.PersonaEmiteId)
        : null,
      EmpresaRecibeId: form.EmpresaRecibeId
        ? Number(form.EmpresaRecibeId)
        : null,
      PersonaRecibeId: form.PersonaRecibeId
        ? Number(form.PersonaRecibeId)
        : null,
      PersonaRecibeNombreLibre: form.PersonaRecibeId
        ? null
        : form.PersonaRecibeNombreLibre.trim() || null,
      ConceptoId: Number(form.ConceptoId),
      MonedaId: Number(form.MonedaId),
      Importe: Number(form.Importe),
      FechaInicio: normalizarFechaInput(form.FechaInicio) || null,
      FechaFin: normalizarFechaInput(form.FechaFin) || null,
      PdfPath: null,
      CajaId: formaPagoEsEfectivo ? Number(form.CajaId) : null,
      ReciboDuplicadoOrigenId: incluirDuplicado
        ? reciboBaseDuplicado?.Id || null
        : null,
      FolioDuplicadoOrigen: incluirDuplicado
        ? reciboBaseDuplicado?.Folio || null
        : null
    };
  }

  function cerrarPreviewModal() {
    setPreviewModal({
      abierto: false,
      mensaje: ''
    });
  }

  async function generarVistaPrevia() {
    setMensaje('');
    setErroresValidacion([]);
    setPreviewModal({ abierto: false, mensaje: '' });

    const errores = validarAntesDeGuardar();

    if (errores.length > 0) {
      setErroresValidacion(errores);
      setPreviewModal({
        abierto: true,
        mensaje: errores.join('\n')
      });
      return;
    }

    const ventanaPreview = window.open('', '_blank');

    if (ventanaPreview) {
      ventanaPreview.document.write('<p style="font-family: Arial, sans-serif; padding: 16px;">Generando vista previa...</p>');
    }

    try {
      setGenerandoPreview(true);

      const response = await api.post(
        '/pdf/recibo/preview',
        construirPayloadRecibo(false),
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);

      if (ventanaPreview && !ventanaPreview.closed) {
        ventanaPreview.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (error) {
      if (ventanaPreview && !ventanaPreview.closed) {
        ventanaPreview.close();
      }

      let mensajeError = 'No fue posible generar la vista previa.';

      if (error.response?.data instanceof Blob) {
        try {
          const textoError = await error.response.data.text();
          const jsonError = JSON.parse(textoError);
          mensajeError = jsonError.mensaje || mensajeError;
        } catch (_) {
          mensajeError = 'No fue posible generar la vista previa.';
        }
      } else {
        mensajeError = error.response?.data?.mensaje || error.message || mensajeError;
      }

      setPreviewModal({
        abierto: true,
        mensaje: mensajeError
      });
    } finally {
      setGenerandoPreview(false);
    }
  }
  async function guardarRecibo(e) {
    e.preventDefault();

    setMensaje('');
    setReciboCreado(null);
    setErroresValidacion([]);

    const errores = validarAntesDeGuardar();

    if (errores.length > 0) {
      setErroresValidacion(errores);
      setMensaje('Revisa los campos obligatorios antes de guardar.');

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

      return;
    }

    try {
      const payload = construirPayloadRecibo(true);

      const ventanaPdf = window.open('', '_blank');

      const res = await api.post('/recibos', payload);

      const reciboNuevo = {
        ...res.data.data,
        Id: res.data.data.Id || res.data.data.ReciboId
      };

      setReciboCreado(reciboNuevo);
      setReciboBaseDuplicado(null);
      setMensaje(`Recibo creado correctamente: ${reciboNuevo.Folio}`);

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      if (reciboNuevo.Id) {
        await generarPdfRecibo(reciboNuevo.Id);
        await abrirPdfRecibo(reciboNuevo.Id, ventanaPdf);
      }

      setTimeout(() => {
        limpiarFormularioConDefaults();
        setMensaje('');
        setReciboCreado(null);

        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 1200);

    } catch (error) {
      setMensaje(error.response?.data?.mensaje || error.message);
    }
  }

  return (
    <div className="page-container-normal recibos-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-1">Recibos</h1>

          <div className="text-muted">
            Captura y generación de recibos
          </div>
        </div>
      </div>

      {mensaje && (
        <div className="alert alert-info">
          {mensaje}
        </div>
      )}

      {erroresValidacion.length > 0 && (
        <div className="alert alert-warning">
          <div className="fw-bold mb-2">
            ⚠ Campos pendientes por validar:
          </div>

          <ul className="mb-0">
            {erroresValidacion.map((error, index) => (
              <li key={index}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {reciboCreado && (
        <div className="card-box mb-3 border border-success">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="text-success mb-1">
                Recibo generado correctamente
              </h5>

              <div>
                <strong>Folio:</strong> {reciboCreado.Folio}
              </div>
            </div>

            <div>
              <button
                className="btn btn-primary"
                type="button"
                onClick={async () => {
                  try {
                    await abrirPdfRecibo(reciboCreado.Id || reciboCreado.ReciboId);
                  } catch (error) {
                    setMensaje(
                      error.response?.data?.mensaje ||
                      'No fue posible abrir el PDF.'
                    );
                  }
                }}
              >
                Ver PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-muted mb-2" style={{ fontSize: '12px' }}>
        <span style={{ color: '#dc3545' }}>*</span> Campo obligatorio
      </div>

      <form onSubmit={guardarRecibo}>
        <div className="card-box recibo-form-card">
          <div className="recibo-grid-4">

            <div className="recibo-field recibo-col-1">
              {etiqueta('Tipo de recibo', true)}

              <select
                className="form-select"
                name="TipoReciboId"
                value={form.TipoReciboId}
                onChange={handleChange}
              >
                <option value="">Seleccione</option>

                {filtrarCatalogo('TipoRecibo').map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Concepto', true)}

              <select
                className="form-select"
                name="ConceptoId"
                value={form.ConceptoId}
                onChange={handleChange}
              >
                <option value="">Seleccione</option>

                {filtrarCatalogo('Concepto').map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Lugar Expedición', true)}

              <select
                className="form-select"
                name="LugarExpedicionId"
                value={form.LugarExpedicionId}
                onChange={handleChange}
              >
                <option value="">Seleccione</option>

                {filtrarCatalogo('LugarExpedicion').map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Fecha', true)}

              <input
                type="date"
                className="form-control"
                name="Fecha"
                value={form.Fecha}
                onChange={handleChange}
              />
            </div>

            <div className="recibo-field recibo-col-2">
              <div className="recibo-field-group-label">Emite</div>

              {campoAutocomplete({
                id: 'autoEmpresaEmite',
                label: 'Empresa',
                obligatorio: false,
                value: textoAuto.EmpresaEmite,
                onChange: actualizarEmpresaEmite,
                opciones: empresasActivas,
                placeholder: 'Escribe para buscar empresa emisora'
              })}
            </div>

            <div className="recibo-field recibo-col-2">
              <div className="recibo-field-group-label recibo-field-group-label-hidden">Emite</div>

              {campoAutocomplete({
                id: 'autoPersonaEmite',
                label: 'Persona',
                obligatorio: false,
                value: textoAuto.PersonaEmite,
                onChange: actualizarPersonaEmite,
                opciones: personasEmisoras,
                placeholder: 'Escribe para buscar persona emisora'
              })}
            </div>

            <div className="recibo-field recibo-col-2">
              <div className="recibo-field-group-label">Recibe</div>

              {campoAutocomplete({
                id: 'autoEmpresaRecibe',
                label: 'Empresa',
                obligatorio: false,
                value: textoAuto.EmpresaRecibe,
                onChange: actualizarEmpresaRecibe,
                opciones: empresasActivas,
                placeholder: 'Escribe para buscar empresa receptora'
              })}
            </div>

            <div className="recibo-field recibo-col-2">
              <div className="recibo-field-group-label recibo-field-group-label-hidden">Recibe</div>

              {campoAutocomplete({
                id: 'autoPersonaRecibe',
                label: 'Persona',
                obligatorio: false,
                value: textoAuto.PersonaRecibe,
                onChange: actualizarPersonaRecibe,
                opciones: personasReceptoras,
                placeholder: 'Escribe para buscar o capturar persona ocasional',
                ayuda: form.PersonaRecibeNombreLibre
                  ? 'Persona ocasional: se guardará solo en este recibo.'
                  : ''
              })}
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Moneda', true)}

              <select
                className="form-select"
                name="MonedaId"
                value={form.MonedaId}
                onChange={handleChange}
              >
                <option value="">Seleccione</option>

                {filtrarCatalogo('Moneda').map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Importe', true)}

              <input
                type="number"
                step="0.01"
                className="form-control money-input"
                name="Importe"
                value={form.Importe}
                onChange={handleChange}
              />
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Forma de Pago', true)}

              <select
                className="form-select"
                name="FormaPagoId"
                value={form.FormaPagoId}
                onChange={handleChange}
              >
                <option value="">Seleccione</option>

                {filtrarCatalogo('FormaPago').map(item => (
                  <option key={item.Id} value={item.Id}>
                    {item.Valor}
                  </option>
                ))}
              </select>
            </div>

            <div className="recibo-field recibo-col-1">
              {etiqueta('Referencia', false)}

              <input
                type="text"
                className="form-control"
                name="NumeroReferencia"
                value={form.NumeroReferencia}
                onChange={handleChange}
              />
            </div>
            {formaPagoEsEfectivo && (
              <div className="recibo-field recibo-col-2">
                {etiqueta('Caja', true)}

                <select
                  className="form-select"
                  name="CajaId"
                  value={form.CajaId}
                  onChange={handleChange}
                >
                  <option value="">Seleccione caja</option>
                  {cajasDisponibles.map(caja => (
                    <option key={caja.Id} value={caja.Id}>
                      {caja.Nombre} - saldo {Number(caja.SaldoActual || 0).toLocaleString('es-MX', { style: 'currency', currency: String(caja.Moneda || '').toUpperCase().includes('USD') ? 'USD' : 'MXN' })}
                    </option>
                  ))}
                </select>

                <div className={saldoPosteriorCaja !== null && saldoPosteriorCaja < 0 ? 'form-text text-danger' : 'form-text'}>
                  Saldo disponible: <span className="money-value d-inline-block">{saldoCaja === null ? '-' : Number(saldoCaja).toLocaleString('es-MX', { style: 'currency', currency: String(cajaSeleccionada?.Moneda || '').toUpperCase().includes('USD') ? 'USD' : 'MXN' })}</span>
                  {' '}| Saldo posterior estimado: <span className="money-value d-inline-block">{saldoPosteriorCaja === null ? '-' : Number(saldoPosteriorCaja).toLocaleString('es-MX', { style: 'currency', currency: String(cajaSeleccionada?.Moneda || '').toUpperCase().includes('USD') ? 'USD' : 'MXN' })}</span>
                </div>
              </div>
            )}

            <div className="recibo-field recibo-col-1">
              <div className="recibo-field-group-label">Vigencia</div>

              {etiqueta('Fecha Inicio', false)}

              <input
                type="date"
                className="form-control"
                name="FechaInicio"
                value={form.FechaInicio}
                onChange={handleChange}
              />
            </div>

            <div className="recibo-field recibo-col-1">
              <div className="recibo-field-group-label recibo-field-group-label-hidden">Vigencia</div>

              {etiqueta('Fecha Fin', false)}

              <input
                type="date"
                className="form-control"
                name="FechaFin"
                value={form.FechaFin}
                onChange={handleChange}
              />
            </div>

            <div className="recibo-field recibo-col-2 recibo-observaciones">
              {etiqueta('Observaciones', false)}

              <textarea
                className="form-control"
                name="Observaciones"
                value={form.Observaciones}
                onChange={handleChange}
              />
            </div>

          </div>
        </div>

        <div className="recibo-actions">
          <button
            type="button"
            className="btn btn-outline-primary px-4"
            disabled={generandoPreview}
            onClick={generarVistaPrevia}
          >
            {generandoPreview ? 'Generando vista previa...' : 'Vista previa'}
          </button>

          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={generandoPreview}
          >
            Guardar recibo
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={generandoPreview}
            onClick={limpiarFormularioConDefaults}
          >
            Limpiar
          </button>
        </div>
      </form>

      <AppModal
        abierto={previewModal.abierto}
        titulo="Vista previa"
        mensaje={previewModal.mensaje}
        variante="warning"
        mostrarCancelar={false}
        textoAceptar="Aceptar"
        onCancelar={cerrarPreviewModal}
        onAceptar={cerrarPreviewModal}
      />
    </div>
  );

}

export default RecibosPage;



















